import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Users, Loader2 } from "lucide-react";

const ScoreBadge = ({ passed, score }) => (
  <span
    className={
      "text-xs font-semibold px-2 py-0.5 rounded-sm border " +
      (passed
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-red-50 text-red-700 border-red-200")
    }
  >
    {score}%
  </span>
);

export default function CourseProgress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tests/attempts", { params: { course_id: id } });
        setAttempts(data);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate(-1);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const filtered = attempts.filter((a) => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return (
      (a.user_email || "").toLowerCase().includes(q) ||
      (a.user_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="course-progress-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
        >
          <ArrowLeft size={16} /> Indietro
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <Users size={14} /> Progressi
          </div>
          <h1 className="text-3xl text-brand-ink mb-1">Tentativi degli studenti</h1>
          <p className="text-sm text-brand-muted">{attempts.length} tentativ{attempts.length === 1 ? "o" : "i"} totali su questo corso.</p>
        </div>

        <div className="mb-4">
          <input
            data-testid="progress-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtra per nome o email…"
            className="w-full max-w-sm h-10 rounded-sm border border-brand-line px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-brand-muted">
            <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…
          </div>
        ) : (
          <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brand-bg border-b border-brand-line text-brand-muted">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Studente</th>
                  <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold">Modulo</th>
                  <th className="text-left px-4 py-3 font-semibold">Tentativo</th>
                  <th className="text-left px-4 py-3 font-semibold">Data</th>
                  <th className="text-left px-4 py-3 font-semibold">Punteggio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line" data-testid="progress-tbody">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-brand-muted">Nessun tentativo trovato.</td>
                  </tr>
                ) : (
                  filtered.map((a) => (
                    <tr key={a.id} className="hover:bg-brand-bg">
                      <td className="px-4 py-3">
                        <div className="text-brand-ink">{a.user_name || "—"}</div>
                        <div className="text-xs text-brand-muted">{a.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">
                          {a.test_type === "intermediate" ? "Intermedio" : "Finale"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-brand-ink">{a.module_title || "—"}</td>
                      <td className="px-4 py-3 text-brand-ink">#{a.attempt_number}</td>
                      <td className="px-4 py-3 text-brand-muted text-xs">
                        {a.submitted_at ? new Date(a.submitted_at).toLocaleString("it-IT") : "In corso"}
                      </td>
                      <td className="px-4 py-3">
                        {a.submitted_at ? (
                          <ScoreBadge passed={a.passed} score={a.score ?? 0} />
                        ) : (
                          <span className="text-xs text-amber-700 font-semibold">In corso</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
